import {
  createContext,
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";

import Lenis from "lenis";

import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/i18n";
import { ContentProvider } from "@/content";

import { Intro } from "@/components/Intro";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

import Home from "@/pages/Home";
import ProjectDetail from "@/pages/ProjectDetail";
import Studio from "@/pages/Studio";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

const AdminApp = lazy(() => import("@/admin/AdminApp"));

export const IntroContext = createContext(false);

let lenis = null;

const Site = () => {
  const location = useLocation();
  const reduced = useReducedMotion();

  const isAdmin = location.pathname.startsWith("/admin");

  const [intro, setIntro] = useState(
    () =>
      !sessionStorage.getItem("solidor-intro") &&
      !reduced &&
      window.location.pathname === "/"
  );

  useEffect(() => {
    if (reduced) return undefined;

    lenis = new Lenis({
      duration: 1.15,
      smoothWheel: true,
    });

    let raf;

    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);

      if (lenis) {
        lenis.destroy();
        lenis = null;
      }
    };
  }, [reduced]);

  useEffect(() => {
    if (lenis) {
      lenis.scrollTo(0, {
        immediate: true,
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const firstLoad = useRef(true);

  const [curtain, setCurtain] = useState(false);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return undefined;
    }

    setCurtain(true);

    const timer = setTimeout(() => {
      setCurtain(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = intro ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [intro]);

  const finishIntro = () => {
    sessionStorage.setItem("solidor-intro", "1");
    setIntro(false);
  };

  return (
    <IntroContext.Provider value={intro}>
      <AnimatePresence>
        {intro && (
          <Intro
            key="intro"
            onDone={finishIntro}
          />
        )}
      </AnimatePresence>

      {!isAdmin && <Navbar />}

      <AnimatePresence>
        {curtain && !isAdmin && (
          <motion.div
            key={location.pathname}
            data-testid="page-transition-curtain"
            className="pointer-events-none fixed inset-0 z-[90] bg-solidor"
            initial={{
              y: "100%",
            }}
            animate={{
              y: ["100%", "0%", "-100%"],
            }}
            transition={{
              duration: 1.15,
              times: [0, 0.45, 1],
              ease: [0.76, 0, 0.24, 1],
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <Routes
          location={location}
          key={
            location.pathname.startsWith("/admin")
              ? "admin"
              : location.pathname
          }
        >
          <Route
            path="/"
            element={<Home />}
          />

          <Route
            path="/projects/:id"
            element={<ProjectDetail />}
          />

          <Route
            path="/project/:id"
            element={<ProjectDetail />}
          />

          <Route
            path="/studio"
            element={<Studio />}
          />

          <Route
            path="/services"
            element={<Services />}
          />

          <Route
            path="/contact"
            element={<Contact />}
          />

          <Route
            path="/admin/*"
            element={
              <Suspense
                fallback={
                  <div className="min-h-screen bg-[#F9F8F6]" />
                }
              >
                <AdminApp />
              </Suspense>
            }
          />

          <Route
            path="*"
            element={<NotFound />}
          />
        </Routes>
      </AnimatePresence>

      {!isAdmin && <Footer />}

      <Toaster position="bottom-center" />
    </IntroContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <ContentProvider>
          <Site />
        </ContentProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
